import { Immutable } from "immer";

import type { IStringAttribute, InternalProduct } from "../models/catalogue";
import type { FinancingPlans } from "../models/financing";
import { isoMonths, isoProductID } from "../models/nominals";
import type { IPrice } from "../models/prices";
import {
    ATTRIBUTE_CLASSES,
    AbstractProductTileData,
} from "../models/shop-compare";
import { PriceType, getProductPrice } from "./catalogue";
import { filterFinancingPlans, getLongestFinancingPlan } from "./financing";
import { decimal } from "./format";
import { getMonthlyPrice, toUnit } from "./money";
import { SortType, getMasterSortList } from "./sorting";

const FEEL = "option_feel";
const SIZE = "option_size";

/**
 * The price every shop/compare tile renders. The resolver ranks candidates on
 * these same options so that "cheapest" is measured in the dollars the tile
 * shows; import this rather than repeating the triple at each call site.
 */
export const TILE_PRICE_OPTIONS = {
    priceType: PriceType.COSMETIC_EXCL_TAX,
    includePostDiscountAddons: true,
    quantity: 1,
} as const;

/**
 * Feel preference, most preferred first. Per the #35909 requirement owner
 * (2026-09-10), a price tie resolves to Medium and then Medium Hybrid; the
 * site's standard preselect order decides the rest. The two leading entries
 * reappear in that order, but `indexOf` never reaches the duplicates.
 */
const FEEL_ORDER: readonly string[] = [
    ATTRIBUTE_CLASSES.medium,
    ATTRIBUTE_CLASSES.hybrid,
    ...(getMasterSortList(SortType.PRESELECT)[FEEL] ?? []),
];

export type TileVariant = Immutable<{
    link: InternalProduct["link"];
    price: InternalProduct["price"];
    feel: string | null;
}>;

const getAttrValue = (
    child: Immutable<InternalProduct>,
    code: string,
): string | null => {
    const attrs = child.attributes;
    const attr = Array.isArray(attrs)
        ? attrs.find((a: IStringAttribute) => a.code === code)
        : attrs[code];
    const value = attr?.value;
    return typeof value === "string" ? value : null;
};

const getDisplayAmount = (child: Immutable<InternalProduct>): number =>
    toUnit(getProductPrice(child.price, TILE_PRICE_OPTIONS));

const getFeelRank = (feel: string | null): number => {
    const index = FEEL_ORDER.indexOf(feel ?? "");
    return index >= 0 ? index : FEEL_ORDER.length;
};

const applyTileFinancingPlans = (
    price: Immutable<IPrice>,
    financingPlans?: Immutable<FinancingPlans>,
): Immutable<IPrice> => {
    // Defensive: tiles without a product carry no `price` at runtime even
    // though the type says it is always present.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!price?.per_month_term_length || !financingPlans?.length) {
        return price;
    }
    const longestActivePlan = getLongestFinancingPlan(
        filterFinancingPlans(financingPlans),
    );
    if (
        !longestActivePlan ||
        isoMonths.unwrap(longestActivePlan.term_months) <= 0
    ) {
        return price;
    }
    const totalPrice = getProductPrice(price, TILE_PRICE_OPTIONS);
    return {
        ...price,
        per_month_term_length: longestActivePlan.term_months,
        per_month: decimal(
            getMonthlyPrice(totalPrice, longestActivePlan.term_months),
        ),
    };
};

/**
 * Resolve the single child variant a tile represents at the selected size, so
 * that the tile's price and its links always describe the same product.
 *
 * The cheapest *available* feel wins, ties breaking by {@link FEEL_ORDER}
 * and then by id. Unbuyable children are excluded first: their price
 * serializes as null and reads back as zero, which would otherwise make them
 * the cheapest candidate at every size.
 *
 * A selected size with no purchasable child resolves to null, which the tiles
 * render as unavailable.
 *
 * With no size selected the tile itself stands in, and that path is the one
 * exception to the guarantee above: the link is the parent PDP while the price
 * is whatever the server picked for a parent product. It is unreachable while
 * any tile in the set carries size options, so mattress tilesets never take it.
 */
export const resolveTileVariant = (
    tile: Immutable<AbstractProductTileData>,
    selectedSize: string,
): TileVariant | null => {
    if (!selectedSize) {
        return {
            link: tile.link,
            price: applyTileFinancingPlans(tile.price, tile.financing_plans),
            feel: null,
        };
    }

    const [best] = (tile.children ?? [])
        .filter(
            (child) =>
                getAttrValue(child, SIZE) === selectedSize &&
                child.availability.is_available_to_buy,
        )
        .map((child) => {
            const feel = getAttrValue(child, FEEL);
            return {
                child,
                feel,
                amount: getDisplayAmount(child),
                feelRank: getFeelRank(feel),
            };
        })
        .sort(
            (a, b) =>
                a.amount - b.amount ||
                a.feelRank - b.feelRank ||
                isoProductID.unwrap(a.child.id) -
                    isoProductID.unwrap(b.child.id),
        );

    return best
        ? {
              link: best.child.link,
              price: applyTileFinancingPlans(
                  best.child.price,
                  tile.financing_plans,
              ),
              feel: best.feel,
          }
        : null;
};
